/**
 * v3.5.19 — Card surface helper (Frosted / Clear / Solid).
 *
 * Single source of truth for the visual treatment of every welcome /
 * feedback card rendered on top of a wallpaper. Replaces ~10 duplicated
 * `bg-white/5 backdrop-blur-md border border-white/10` strings scattered
 * across ReviewShield, GlobalReview, SurveyPublic, and LanguageGate.
 *
 * Three styles, settable per-organization (voucher_settings.card_style)
 * with optional per-card override (cards.card_style):
 *   • frosted (default) — translucent + backdrop-blur. Wallpaper visible
 *                         but blurred behind the card. The look users had
 *                         pre-v3.5.19.
 *   • clear              — translucent, NO backdrop-blur. Wallpaper shows
 *                         through the card crisply. Picked when the
 *                         organization wants to show off its wallpaper.
 *   • solid              — fully opaque card. Wallpaper visible only
 *                         around the card, never through it. Highest
 *                         text contrast.
 *
 * The helper accepts a variant ('dark' | 'light') because the same three
 * styles map to different colour treatments depending on the page chrome.
 * Most public flows use 'dark' (rendered above BrandingShell's dark
 * gradient / wallpaper).
 */

export const CARD_STYLES = ['frosted', 'clear', 'solid'];
export const DEFAULT_CARD_STYLE = 'frosted';

const SURFACES = {
    dark: {
        frosted: 'bg-white/5 backdrop-blur-md border border-white/10',
        clear: 'bg-white/0 border border-white/15',
        solid: 'bg-slate-900 border border-slate-800',
    },
    light: {
        frosted: 'bg-white/85 dark:bg-slate-800/80 backdrop-blur-md border border-white/40 dark:border-slate-700 shadow-sm',
        clear: 'bg-white/40 dark:bg-slate-800/30 border border-white/30 dark:border-slate-700/40',
        solid: 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm',
    },
};

/**
 * Returns the Tailwind class string for a card surface.
 *
 * @param {'frosted'|'clear'|'solid'|null|undefined} style — falls back to default
 * @param {'dark'|'light'} variant
 * @returns {string}
 */
export function cardSurfaceClass(style, variant = 'dark') {
    const safeStyle = CARD_STYLES.includes(style) ? style : DEFAULT_CARD_STYLE;
    const family = SURFACES[variant] || SURFACES.dark;
    return family[safeStyle] || family[DEFAULT_CARD_STYLE];
}

export default cardSurfaceClass;
